import { useState } from 'react';
import {
  makeStyles, shorthands, tokens, Text, Badge, Button, Link, Spinner,
  MessageBar, MessageBarBody, MessageBarTitle,
  Accordion, AccordionItem, AccordionHeader, AccordionPanel,
} from '@fluentui/react-components';
import {
  CheckmarkCircle20Filled, Warning20Filled, DismissCircle20Filled,
  QuestionCircle20Regular, Person20Regular, Copy20Regular,
} from '@fluentui/react-icons';
import { useApi } from '../api/context';
import { useAsync } from '../api/useAsync';
import { Loading, ErrorState } from './common';
import type { ReadinessStatus } from '../api/client';

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalM },
  head: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  items: {
    display: 'grid', gridTemplateColumns: 'minmax(220px, auto) 1fr',
    columnGap: tokens.spacingHorizontalM, rowGap: tokens.spacingVerticalXXS,
    marginTop: tokens.spacingVerticalS,
  },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 },
  code: {
    fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    whiteSpace: 'pre-wrap', overflowX: 'auto',
  },
  actions: { display: 'flex', columnGap: tokens.spacingHorizontalS, flexWrap: 'wrap', marginTop: tokens.spacingVerticalS },
});

function icon(status: ReadinessStatus) {
  switch (status) {
    case 'ready':   return <CheckmarkCircle20Filled primaryFill={tokens.colorStatusSuccessForeground1} />;
    case 'partial': return <Warning20Filled primaryFill={tokens.colorStatusWarningForeground1} />;
    case 'blocked': return <DismissCircle20Filled primaryFill={tokens.colorStatusDangerForeground1} />;
    case 'manual':  return <Person20Regular />;
    default:        return <QuestionCircle20Regular />;
  }
}

/**
 * What is stopping a collection for this customer.
 *
 * Everything here was previously worked out by running Azure CLI commands
 * against the right directory by hand, where getting the directory wrong
 * produced errors that named the symptom rather than the cause.
 *
 * Two things stay manual because they cannot be otherwise. Consent must be
 * granted by the customer's own Global Administrator; an application able to
 * grant itself permissions in a customer tenant would be the vulnerability.
 * Exchange RBAC is not exposed through any API at all. For both, the page
 * hands over the exact link or command with the customer's values already in
 * it, so nobody has to compose one.
 */
export function Readiness({ tenantId }: { tenantId: string }) {
  const styles = useStyles();
  const api = useApi();
  const readiness = useAsync(() => api.getReadiness(tenantId), [tenantId]);
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (what: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(null), 2000);
  };

  if (readiness.loading && !readiness.data) return <Loading label="Checking readiness" />;
  if (readiness.error) return <ErrorState error={readiness.error} />;
  if (!readiness.data) return null;

  const r = readiness.data;

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <Text weight="semibold" size={400}>Readiness</Text>
        <Badge
          appearance="tint"
          color={r.overall === 'ready' ? 'success' : r.overall === 'blocked' ? 'danger' : 'warning'}
        >
          {r.overall === 'ready' ? 'Ready to collect'
            : r.overall === 'blocked' ? 'Cannot collect'
            : 'Will collect partially'}
        </Badge>
        <Button size="small" appearance="subtle" onClick={() => readiness.reload()}>
          Recheck
        </Button>
        {readiness.loading && <Spinner size="tiny" />}
      </div>

      <Accordion multiple collapsible>
        {r.checks.map((c) => (
          <AccordionItem key={c.id} value={c.id}>
            <AccordionHeader icon={icon(c.status)}>
              <div className={styles.head}>
                <Text>{c.title}</Text>
                <span className={styles.muted}>{c.detail}</span>
              </div>
            </AccordionHeader>

            <AccordionPanel>
              {c.guidance && <Text size={300} block>{c.guidance}</Text>}

              {c.items && c.items.length > 0 && (
                <div className={styles.items}>
                  {c.items.map((i) => (
                    <div key={i.name} style={{ display: 'contents' }}>
                      <span className={styles.mono}>{i.name}</span>
                      <Text size={200} className={styles.muted}>{i.unlocks}</Text>
                    </div>
                  ))}
                </div>
              )}

              {c.id === 'consent-recorded' && (
                <div className={styles.actions}>
                  <Button size="small" icon={<Copy20Regular />}
                          onClick={() => void copy('consent', r.consentUrl)}>
                    {copied === 'consent' ? 'Copied' : 'Copy consent link'}
                  </Button>
                  <Link href={r.portalUrl} target="_blank" rel="noreferrer">
                    <Button size="small">Open in Entra portal</Button>
                  </Link>
                </div>
              )}

              {c.id === 'permissions' && (
                <div className={styles.actions}>
                  {/* The admin consent endpoint refuses when a service
                      principal already exists in the customer directory, which
                      is easy to reach after any earlier attempt. The portal
                      path works either way. */}
                  <Link href={r.portalUrl} target="_blank" rel="noreferrer">
                    <Button size="small">Grant in Entra portal</Button>
                  </Link>
                  <Button size="small" icon={<Copy20Regular />}
                          onClick={() => void copy('consent', r.consentUrl)}>
                    {copied === 'consent' ? 'Copied' : 'Copy consent link'}
                  </Button>
                </div>
              )}

              {c.id === 'exchange-role' && (
                <>
                  <div className={styles.code}>{r.exchangeCommand}</div>
                  <div className={styles.actions}>
                    <Button size="small" icon={<Copy20Regular />}
                            onClick={() => void copy('exchange', r.exchangeCommand)}>
                      {copied === 'exchange' ? 'Copied' : 'Copy command'}
                    </Button>
                  </div>
                </>
              )}
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>

      {r.overall !== 'ready' && (
        <MessageBar intent={r.overall === 'blocked' ? 'error' : 'warning'}>
          <MessageBarBody>
            <MessageBarTitle>
              {r.overall === 'blocked'
                ? 'A collection would return nothing'
                : 'A collection would be partial'}
            </MessageBarTitle>
            Whatever is missing above means the collectors that depend on it are skipped.
            Those checks are reported as not assessed rather than passing, so a partial
            collection is safe to run; it just covers less.
          </MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}
